/**
 * Consolidated Field Operators
 * Single source of truth for filter operators used across DataGrid and Advanced Search
 */

import {
  GridFilterOperator,
  GridFilterInputValueProps,
} from '@mui/x-data-grid-premium';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import {
  OPERATOR_REGISTRY,
  getOperatorsForDataType,
  LogicalOperator,
} from '../components/TableSearch/types';
import AsyncSearchSelect from '../components/TableSearch/AsyncSearchSelect';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Filter operator type derived from OPERATOR_REGISTRY
 * Ensures type safety and alignment with supported operators
 */
export type FilterOperator = keyof typeof OPERATOR_REGISTRY;

/**
 * Filter rule for advanced search queries
 * Used in filterGroups structure sent to backend
 */
export interface FilterRule {
  id: string;
  field: string;
  operator: FilterOperator;
  value: string | string[];
}

/**
 * Filter group containing multiple rules
 * Rules within a group are combined with logicalOperator
 */
export interface FilterGroup {
  id: string;
  logicalOperator: LogicalOperator;
  rules: FilterRule[];
}

/**
 * Complete advanced search query structure
 * Groups are combined with groupOperator
 */
export interface AdvancedSearchQuery {
  filterGroups: FilterGroup[];
  groupOperator: LogicalOperator;
}

// Re-export for convenience
export { getOperatorsForDataType };
export type { LogicalOperator };

// ============================================================================
// DATAGRID FILTER OPERATORS
// ============================================================================

/**
 * Create a standard text input filter operator for MUI DataGrid
 */
function createTextFilterOperator(
  label: string,
  value: string
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const {
        apiRef: _apiRef,
        applyValue,
        focusElementRef: _focusElementRef,
        ...textFieldProps
      } = props;
      return (
        <TextField
          {...textFieldProps}
          variant="standard"
          placeholder="Filter value"
          onChange={e => applyValue({ ...props.item, value: e.target.value })}
        />
      );
    },
  };
}

/**
 * Create a number input filter operator for MUI DataGrid
 */
function createNumberFilterOperator(
  label: string,
  value: string
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const {
        apiRef: _apiRef,
        applyValue,
        focusElementRef: _focusElementRef,
        ...textFieldProps
      } = props;
      return (
        <TextField
          {...textFieldProps}
          type="number"
          variant="standard"
          placeholder="Filter value"
          onChange={e => applyValue({ ...props.item, value: e.target.value })}
        />
      );
    },
  };
}

/**
 * Create a date input filter operator for MUI DataGrid
 */
function createDateFilterOperator(
  label: string,
  value: string
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const {
        apiRef: _apiRef,
        applyValue,
        focusElementRef: _focusElementRef,
        ...textFieldProps
      } = props;
      return (
        <TextField
          {...textFieldProps}
          type="date"
          variant="standard"
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={e => applyValue({ ...props.item, value: e.target.value })}
        />
      );
    },
  };
}

/**
 * Create a selection input filter operator for MUI DataGrid
 * @param label - Display label for the operator
 * @param value - Operator value (equals or notEquals)
 * @param options - Array of {value, label} options for the select dropdown
 */
function createSelectionFilterOperator(
  label: string,
  value: string,
  options: Array<{ value: string; label: string }>
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const {
        apiRef: _apiRef,
        applyValue,
        focusElementRef: _focusElementRef,
        ...restProps
      } = props;
      return (
        <Select
          {...restProps}
          variant="standard"
          value={props.item.value || ''}
          onChange={e => applyValue({ ...props.item, value: e.target.value })}
          fullWidth>
          <MenuItem value="">
            <em>All</em>
          </MenuItem>
          {options.map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      );
    },
  };
}

/**
 * Create an async search filter operator for MUI DataGrid
 * Used for relational fields that load data from API endpoints
 * @param label - Display label for the operator
 * @param value - Operator value (contains, equals, etc.)
 * @param apiEndpoint - API endpoint to fetch options from
 * @param searchField - Field name to search in
 * @param valueField - Field to use as value (default: 'id')
 * @param labelField - Field to use as label (default: 'name')
 */
function createAsyncSearchFilterOperator(
  label: string,
  value: string,
  apiEndpoint: string,
  searchField: string,
  valueField: string = 'id',
  labelField: string = 'name'
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const {
        apiRef: _apiRef,
        applyValue,
        focusElementRef: _focusElementRef,
        ...restProps
      } = props;
      return (
        <AsyncSearchSelect
          {...restProps}
          value={props.item.value || ''}
          onChange={(newValue: string | null) =>
            applyValue({ ...props.item, value: newValue || '' })
          }
          apiEndpoint={apiEndpoint}
          searchField={searchField}
          valueField={valueField}
          labelField={labelField}
          placeholder="Search..."
        />
      );
    },
  };
}

/**
 * Create a date range filter operator for MUI DataGrid (for isBetween)
 */
function createDateRangeFilterOperator(): GridFilterOperator {
  return {
    label: OPERATOR_REGISTRY.isBetween.label,
    value: 'isBetween',
    getApplyFilterFn: () => null, // Filtering handled by backend
    InputComponent: (props: GridFilterInputValueProps) => {
      // Exclude invalid DOM props
      const values = Array.isArray(props.item.value)
        ? props.item.value
        : ['', ''];
      return (
        <div style={{ display: 'flex', gap: '8px', flexDirection: 'column' }}>
          <TextField
            type="date"
            variant="standard"
            placeholder="Start date"
            value={values[0] || ''}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={e =>
              props.applyValue({
                ...props.item,
                value: [e.target.value, values[1]],
              })
            }
          />
          <TextField
            type="date"
            variant="standard"
            placeholder="End date"
            value={values[1] || ''}
            slotProps={{ inputLabel: { shrink: true } }}
            onChange={e =>
              props.applyValue({
                ...props.item,
                value: [values[0], e.target.value],
              })
            }
          />
        </div>
      );
    },
  };
}

/**
 * Create a no-value filter operator for MUI DataGrid (isEmpty / isNotEmpty).
 * requiresFilterValue: false tells MUI X not to render the value input column.
 */
function createNoValueFilterOperator(
  label: string,
  value: string
): GridFilterOperator {
  return {
    label,
    value,
    getApplyFilterFn: () => null, // Filtering handled by backend
    requiresFilterValue: false, // Hides the value input field in MUI X filter panel
  };
}

/**
 * String filter operators for MUI DataGrid
 * Labels synchronized with OPERATOR_REGISTRY
 */
export const stringGridFilterOperators: GridFilterOperator[] = [
  createTextFilterOperator(OPERATOR_REGISTRY.contains.label, 'contains'),
  createTextFilterOperator(OPERATOR_REGISTRY.equals.label, 'equals'),
  createTextFilterOperator(OPERATOR_REGISTRY.notEquals.label, 'notEquals'),
  createNoValueFilterOperator(OPERATOR_REGISTRY.isEmpty.label, 'isEmpty'),
  createNoValueFilterOperator(OPERATOR_REGISTRY.isNotEmpty.label, 'isNotEmpty'),
];

/**
 * Date filter operators for MUI DataGrid
 * Labels synchronized with OPERATOR_REGISTRY
 */
// created_date / modified_date are DATETIME2 NOT NULL with DB defaults —
// isEmpty/isNotEmpty are excluded for date columns (would always return 0 rows).
export const dateGridFilterOperators: GridFilterOperator[] = [
  createDateFilterOperator(OPERATOR_REGISTRY.equals.label, 'equals'),
  createDateFilterOperator(OPERATOR_REGISTRY.isBefore.label, 'isBefore'),
  createDateFilterOperator(OPERATOR_REGISTRY.isAfter.label, 'isAfter'),
  createDateRangeFilterOperator(),
];

/**
 * Number filter operators for MUI DataGrid
 * Labels synchronized with OPERATOR_REGISTRY
 */
// No nullable number fields exist in this codebase — a NOT NULL number can
// never be empty, so isEmpty / isNotEmpty are excluded for number columns.
export const numberGridFilterOperators: GridFilterOperator[] = [
  createNumberFilterOperator(OPERATOR_REGISTRY.equals.label, 'equals'),
  createNumberFilterOperator(OPERATOR_REGISTRY.notEquals.label, 'notEquals'),
  createNumberFilterOperator(
    OPERATOR_REGISTRY.greaterThan.label,
    'greaterThan'
  ),
  createNumberFilterOperator(OPERATOR_REGISTRY.lessThan.label, 'lessThan'),
];

/**
 * Async search filter operators for MUI DataGrid (relational fields)
 * @param apiEndpoint - API endpoint to fetch options from
 * @param searchField - Field name to search in
 * @param valueField - Field to use as value (default: 'id')
 * @param labelField - Field to use as label (default: 'name')
 */
export function getAsyncSearchFilterOperators(
  apiEndpoint: string,
  searchField: string,
  valueField: string = 'id',
  labelField: string = 'name'
): GridFilterOperator[] {
  return [
    createAsyncSearchFilterOperator(
      OPERATOR_REGISTRY.contains.label,
      'contains',
      apiEndpoint,
      searchField,
      valueField,
      labelField
    ),
    createAsyncSearchFilterOperator(
      OPERATOR_REGISTRY.equals.label,
      'equals',
      apiEndpoint,
      searchField,
      valueField,
      labelField
    ),
  ];
}

/**
 * Get MUI DataGrid filter operators based on field data type
 * @param dataType - The data type of the field
 * @param options - Optional selection options for selection fields
 */
export function getGridFilterOperatorsForType(
  dataType: 'string' | 'number' | 'date' | 'selection',
  options?: Array<{ value: string; label: string }>
): GridFilterOperator[] {
  switch (dataType) {
    case 'string':
      return stringGridFilterOperators;
    case 'number':
      return numberGridFilterOperators;
    case 'date':
      return dateGridFilterOperators;
    case 'selection':
      return options
        ? [
            createSelectionFilterOperator(
              OPERATOR_REGISTRY.equals.label,
              'equals',
              options
            ),
            createSelectionFilterOperator(
              OPERATOR_REGISTRY.notEquals.label,
              'notEquals',
              options
            ),
          ]
        : stringGridFilterOperators;
    default:
      return stringGridFilterOperators;
  }
}
